/*
 * From http://www.redblobgames.com/x/1744-rendering-game-elements/
 * Copyright 2017 Red Blob Games <redblobgames@gmail.com>
 * License: Apache v2.0 <http://www.apache.org/licenses/LICENSE-2.0.html>
 */
'use strict';

/* global d3 */

const SimplexNoise = require('simplex-noise');
const DualMesh =     require('@redblobgames/dual-mesh');
const createMesh =   require('@redblobgames/dual-mesh/create');
const MapGen =       require('@redblobgames/mapgen2');
const DrawWater =    require('../1742-webgl-mapgen2/draw-water');
const Render =       require('./render');
const {makeRandInt, makeRandFloat} = require('@redblobgames/prng');


let param = {
    seed: 24,
    variant: 0,
    spacing: 21,
    temperature: 0,
    rainfall: 0,
};


function generateColors(N) {
    const L = 50;
    const step = 200 / N;
    const mindistance = 50;
    let colors = [];
    for (let a = -100; a <= 100; a += step) {
        for (let b = -100; b <= 100; b += step) {
            if (Math.abs(a) + Math.abs(b) < mindistance) { continue; }
            let color = d3.lab(L, a, b).rgb();
            if (0 <= color.r && color.r <= 255
                && 0 <= color.g && color.g <= 255
                && 0 <= color.b && color.b <= 255) {
                color.opacity = 0.1;
                colors.push(color.toString());
            }
        }
    }
    d3.shuffle(colors);
    return colors;
}
                
                
function assignRandomEmpires(map, r_owner) {
    const colors = generateColors(30);
    const numEmpires = Math.min(50, colors.length);
    console.log(colors.length);
    let {mesh} = map;
    let randInt = makeRandInt(12345);
    let r_out = [];
    for (let i = 0; i < numEmpires; i++) {
        let r = randInt(mesh.numSolidRegions);
        for (let j = randInt(100); j >= 0 && !map.r_water[r] && !mesh.r_ghost(r); j--) {
            r_owner[r] = colors[i];
            mesh.r_circulate_r(r_out, r);
            r = r_out[randInt(r_out.length)];
        }
    }
}

function calculateBorders(map, r_owner) {
    let {mesh} = map;
    let s_path = [];
    let paths = new Map();
    for (let s = 0; s < mesh.numSolidSides; s++) {
        if (s_path[s]) { continue; }
        let owner = r_owner[mesh.s_begin_r(s)];
        if (owner === undefined) { continue; }
        if (owner === r_owner[mesh.s_end_r(s)]) { continue; }
        
        let path = [];
        if (!paths.has(owner)) { paths.set(owner, []); }
        paths.get(owner).push(path);

        /* 
         * This loop is tricky; I worked it out on paper. When we
         * enter the loop, we've moving along the edge s_outer_t(s) →
         * s_inner_t(s), and the owned region is on the left. From
         * s_inner(t) we could take one of two sides. We want to turn
         * left if the region (front_r) has a different owner, and
         * turn right if it has the same owner.
         */
        for (let s2 = s; !s_path[s2]; s2 = mesh.s_opposite_s(s2)) {
            s_path[s2] = path;
            path.push(s2);
            let front_r = mesh.s_end_r(mesh.s_next_s(s2));
            if (owner === r_owner[front_r]) {
                s2 = mesh.s_next_s(s2);
            } else {
                s2 = mesh.s_prev_s(s2);
            }
        }
    }
    return {s_path, paths};
}


let canvas = document.getElementById('map');

function draw() {
    console.time('mesh-init');
    let mesh = new DualMesh(createMesh({spacing: param.spacing, random: makeRandFloat(12345)}));
    console.timeEnd('mesh-init');
    
    console.time('map-init');
    let map = new MapGen(mesh, {amplitude: 0.2, length: 100, seed: 12345}, makeRandInt);
    console.timeEnd('map-init');
    
    let ctx = canvas.getContext('2d');

    console.time('map-gen');
    map.calculate({
        noise: new SimplexNoise(makeRandFloat(param.seed)),
        numRivers: 10,
        drainageSeed: param.variant,
        riverSeed: param.variant,
        biomeBias: {temperature: param.temperature, moisture: param.rainfall},
    });
    console.timeEnd('map-gen');

    map.r_owner = [];
    assignRandomEmpires(map, map.r_owner);
    let {s_path, paths} = calculateBorders(map, map.r_owner);
    
    ctx.clearRect(0, 0, 1000, 1000);

    console.time('draw-regions');
    Render.regions(ctx, map);
    // Render.sides(ctx, map);
    for (let [owner, path] of paths.entries()) {
        Render.path(ctx, map, path, owner);
    }
    
    console.timeEnd('draw-regions');
    
    console.time('draw-lakes');
    DrawWater.lakes(ctx, map);
    console.timeEnd('draw-lakes');
    
    console.time('draw-rivers');
    DrawWater.rivers(ctx, map);
    console.timeEnd('draw-rivers');
}


draw();
